// Approval chains (ADR-0102): the chain editor (About this agent › Approvals, approval steps),
// the level shown to the approver, cover during leave, and the level in What happened.
export default {
  en: {
    editor: {
      chainTitle: "Who approves, in order",
      chainHelp: "Each level is signed in turn by a different person. A level with a condition is asked only when the condition holds. Nobody signs two levels of one request, nor their own request.",
      level: "Level {{n}}",
      levelName: "Name of this level (optional)",
      levelName_en: "Name in English",
      levelName_ar: "Name in Arabic",
      who: "Who signs",
      who_roles: "People with a role",
      who_manager: "The requester's manager",
      who_person: "A person named in the request",
      roles: "Roles",
      rolesHelp: "Anyone with one of these roles can sign.",
      managerOf: "Whose manager",
      managerOfRequester: "The person who asked",
      managerOfHelp: "The manager comes from the company directory. Leave it as is for the person who asked, or pick a value that holds someone else's user name.",
      person: "The value that names the person",
      personHelp: "For example the system's owner from an earlier step. It must hold their user name.",
      fallback: "If nobody is found, ask",
      fallbackHelp: "People with these roles sign instead, also when that person already signed an earlier level.",
      quorum: "How many must approve",
      quorumHelp: "Different people with these roles, for example 2 of the finance team.",
      people_one: "{{count}} person",
      people_other: "{{count}} people",
      when: "Only when",
      whenHelp: "Leave empty to ask this level every time.",
      within: "Decide within",
      unit_hours: "working hours",
      unit_days: "working days",
      withinHelp: "Counted in your organisation's working hours and days. Half-way, a reminder goes out.",
      escalate: "If nobody decides in time, send it to",
      escalateHelp: "Roles in order: each gets the same time again.",
      escalateNone: "Nobody (keep waiting)",
      addLevel: "Add a level",
      removeLevel: "Remove this level",
      up: "Move up",
      down: "Move down",
      platformNote: "The platform's own rules (for example an amount above its limit, or content from outside) add one more level at the end, only when no level above was signed by someone with the role they require.",
      rulesOrder: "Every rule that applies adds its levels, in this order.",
      ruleUp: "Move this rule up",
      ruleDown: "Move this rule down",
      preview: "In short: {{text}}",
      then: "then",
      sum_roles: "{{roles}}",
      sum_quorum: "{{n}} of {{roles}}",
      sum_manager: "the requester's manager",
      sum_person: "the person in {{value}}",
      sum_when: "{{who}} (only when its condition holds)",
      issue: {
        noRole: "Choose at least one role for level {{n}}.",
        noPerson: "Choose the value that names the person for level {{n}}.",
        quorumNotRoles: "Only a level by role can ask several people (level {{n}}).",
        tooMany: "A chain has at most 6 levels.",
      },
    },
    inbox: {
      level: "Level {{level}} of {{of}}",
      source_rule: "Asked by this agent's approval rules",
      source_step: "An approval step of this agent",
      source_platform: "Asked by the platform's rules",
      forManager: "For {{name}}, the requester's manager",
      forPerson: "For {{name}}",
      fallback_no_manager: "The requester's manager could not be found, so it went to {{roles}}.",
      fallback_no_person: "Nobody was named, so it went to {{roles}}.",
      fallback_not_a_person: "The value did not name a person, so it went to {{roles}}.",
      fallback_already_signed: "The named person already signed an earlier level, so it went to {{roles}}.",
      fallback_directory_unavailable: "The company directory did not answer, so it went to {{roles}}.",
      quorum: "{{count}} of {{n}} approvals so far",
      approvedBy: "Approved so far by {{names}}",
      notYou: "You signed an earlier level of this request, or asked for it, so someone else must sign this one.",
      alreadyApproved: "You already approved this. It still needs {{left}} more.",
      onBehalf: "Signed for {{name}} (cover during leave)",
      coveringFor: "You decide this for {{name}}, who is away.",
      chainTitle: "Approval levels",
    },
    cover: {
      open: "Cover while away",
      title: "Cover while I'm away",
      help: "Name someone to decide the approvals addressed to you by name (for example as someone's manager) during your leave. Approvals for your roles still go to the other people with those roles. Every change is recorded.",
      delegate: "Who covers for you",
      period: "When",
      reason: "Reason (optional)",
      save: "Save cover",
      end: "End now",
      none: "No cover set.",
      mine: "Your cover",
      theirs: "You cover for",
      state_active: "Now",
      state_planned: "Planned",
      state_ended: "Ended",
      saved: "Cover saved.",
      ended: "Cover ended.",
      row: "{{name}} · {{from}} – {{until}}",
      failed: "The cover could not be saved: {{reason}}",
      pickPerson: "Choose a person",
      pickPeriod: "Choose the start and end",
    },
    story: {
      level: "level {{level}} of {{of}}",
      levelNamed: "level {{level}} of {{of}} ({{name}})",
      byName: "for {{name}}",
      counted: "{{count}} of {{n}} approvals",
    },
  },
  ar: {
    editor: {
      chainTitle: "من يعتمد، بالترتيب",
      chainHelp: "يوقّع كل مستوى بدوره شخص مختلف. المستوى الذي له شرط يُطلب فقط عندما يتحقق الشرط. لا يوقّع أحد مستويين من الطلب نفسه، ولا يعتمد طلبه بنفسه.",
      level: "المستوى {{n}}",
      levelName: "اسم هذا المستوى (اختياري)",
      levelName_en: "الاسم بالإنجليزية",
      levelName_ar: "الاسم بالعربية",
      who: "من يوقّع",
      who_roles: "أصحاب دور معيّن",
      who_manager: "مدير مقدّم الطلب",
      who_person: "شخص مذكور في الطلب",
      roles: "الأدوار",
      rolesHelp: "يستطيع التوقيع أي شخص لديه أحد هذه الأدوار.",
      managerOf: "مدير من",
      managerOfRequester: "الشخص الذي طلب",
      managerOfHelp: "يُؤخذ المدير من دليل الشركة. اتركه كما هو لمقدّم الطلب، أو اختر قيمة تحمل اسم مستخدم شخص آخر.",
      person: "القيمة التي تحدد الشخص",
      personHelp: "مثل مالك النظام من خطوة سابقة. يجب أن تحمل اسم المستخدم الخاص به.",
      fallback: "إن لم يُعثر على أحد، اطلب من",
      fallbackHelp: "يوقّع أصحاب هذه الأدوار بدلًا منه، وكذلك إن كان ذلك الشخص قد وقّع مستوى سابقًا.",
      quorum: "كم شخصًا يجب أن يعتمد",
      quorumHelp: "أشخاص مختلفون لديهم هذه الأدوار، مثل اثنين من فريق المالية.",
      people_zero: "لا أحد",
      people_one: "شخص واحد",
      people_two: "شخصان",
      people_few: "{{count}} أشخاص",
      people_many: "{{count}} شخصًا",
      people_other: "{{count}} شخص",
      when: "فقط عندما",
      whenHelp: "اتركه فارغًا ليُطلب هذا المستوى في كل مرة.",
      within: "القرار خلال",
      unit_hours: "ساعات عمل",
      unit_days: "أيام عمل",
      withinHelp: "تُحسب بساعات وأيام العمل في مؤسستك. في منتصف المدة يُرسل تذكير.",
      escalate: "إن لم يقرر أحد في الوقت المحدد، أرسله إلى",
      escalateHelp: "أدوار بالترتيب: يحصل كل منها على المدة نفسها مرة أخرى.",
      escalateNone: "لا أحد (يبقى في الانتظار)",
      addLevel: "إضافة مستوى",
      removeLevel: "حذف هذا المستوى",
      up: "تحريك للأعلى",
      down: "تحريك للأسفل",
      platformNote: "تضيف قواعد المنصة نفسها (مثل مبلغ فوق حدّها، أو محتوى من الخارج) مستوى أخيرًا، فقط إذا لم يوقّع أي مستوى أعلاه شخصٌ لديه الدور الذي تطلبه.",
      rulesOrder: "كل قاعدة تنطبق تضيف مستوياتها، بهذا الترتيب.",
      ruleUp: "تحريك هذه القاعدة للأعلى",
      ruleDown: "تحريك هذه القاعدة للأسفل",
      preview: "باختصار: {{text}}",
      then: "ثم",
      sum_roles: "{{roles}}",
      sum_quorum: "{{n}} من {{roles}}",
      sum_manager: "مدير مقدّم الطلب",
      sum_person: "الشخص المذكور في {{value}}",
      sum_when: "{{who}} (فقط عند تحقق شرطه)",
      issue: {
        noRole: "اختر دورًا واحدًا على الأقل للمستوى {{n}}.",
        noPerson: "اختر القيمة التي تحدد الشخص للمستوى {{n}}.",
        quorumNotRoles: "يمكن طلب عدة أشخاص فقط في مستوى حسب الدور (المستوى {{n}}).",
        tooMany: "للسلسلة ستة مستويات على الأكثر.",
      },
    },
    inbox: {
      level: "المستوى {{level}} من {{of}}",
      source_rule: "تطلبه قواعد الاعتماد لهذا الوكيل",
      source_step: "خطوة اعتماد في هذا الوكيل",
      source_platform: "تطلبه قواعد المنصة",
      forManager: "إلى {{name}}، مدير مقدّم الطلب",
      forPerson: "إلى {{name}}",
      fallback_no_manager: "تعذّر العثور على مدير مقدّم الطلب، فذهب إلى {{roles}}.",
      fallback_no_person: "لم يُذكر أحد، فذهب إلى {{roles}}.",
      fallback_not_a_person: "القيمة لا تحدد شخصًا، فذهب إلى {{roles}}.",
      fallback_already_signed: "الشخص المذكور وقّع مستوى سابقًا، فذهب إلى {{roles}}.",
      fallback_directory_unavailable: "لم يستجب دليل الشركة، فذهب إلى {{roles}}.",
      quorum: "{{count}} من {{n}} موافقات حتى الآن",
      approvedBy: "وافق حتى الآن: {{names}}",
      notYou: "وقّعت مستوى سابقًا من هذا الطلب أو أنت من طلبه، لذا يجب أن يوقّع هذا المستوى شخص آخر.",
      alreadyApproved: "سبق أن وافقت. ما زال يحتاج إلى {{left}} آخرين.",
      onBehalf: "وُقّع نيابةً عن {{name}} (تغطية أثناء الإجازة)",
      coveringFor: "تقرّر هذا نيابةً عن {{name}} الغائب.",
      chainTitle: "مستويات الاعتماد",
    },
    cover: {
      open: "تغطية أثناء الغياب",
      title: "من يغطي عني أثناء غيابي",
      help: "حدّد شخصًا يقرّر طلبات الاعتماد الموجّهة إليك بالاسم (مثلًا بصفتك مدير أحدهم) أثناء إجازتك. تبقى طلبات الاعتماد لأدوارك تذهب إلى الآخرين الذين لديهم هذه الأدوار. يُسجَّل كل تغيير.",
      delegate: "من يغطي عنك",
      period: "متى",
      reason: "السبب (اختياري)",
      save: "حفظ التغطية",
      end: "إنهاء الآن",
      none: "لا توجد تغطية.",
      mine: "التغطية عنك",
      theirs: "تغطي عن",
      state_active: "الآن",
      state_planned: "مخطط لها",
      state_ended: "انتهت",
      saved: "حُفظت التغطية.",
      ended: "انتهت التغطية.",
      row: "{{name}} · {{from}} – {{until}}",
      failed: "تعذّر حفظ التغطية: {{reason}}",
      pickPerson: "اختر شخصًا",
      pickPeriod: "اختر البداية والنهاية",
    },
    story: {
      level: "المستوى {{level}} من {{of}}",
      levelNamed: "المستوى {{level}} من {{of}} ({{name}})",
      byName: "إلى {{name}}",
      counted: "{{count}} من {{n}} موافقات",
    },
  },
};
